import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';

const link =
  'font-semibold text-teal-700 underline decoration-teal-600/35 underline-offset-2 transition hover:text-teal-800 hover:decoration-teal-600/60';

export function HelpPage() {
  return (
    <div className="space-y-6">
      <PageHeader
        title="帮助文档"
        description="左侧菜单已按「入门 → 数据 → 站点 → 机器人 → 接入 → 日志」分组。可先扫下方「功能地图」再进各页。"
      />

      <Card title="功能地图（我想做… → 去哪）">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[20rem] border-collapse text-left text-sm text-slate-700">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="py-2 pr-3 font-medium">场景</th>
                <th className="py-2 font-medium">菜单入口</th>
              </tr>
            </thead>
            <tbody className="align-top">
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">看登记、改条目、推草稿</td>
                <td className="py-2">
                  <Link className={link} to="/entries">
                    信息登记
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">换站点、Webhook 密钥、站点级 Android 密钥</td>
                <td className="py-2">
                  <Link className={link} to="/sites">
                    站点管理
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">字段映射、正文模板、贴图/图文体裁</td>
                <td className="py-2">
                  <Link className={link} to="/settings">
                    内容与样式
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">授权公众号写草稿</td>
                <td className="py-2">
                  <Link className={link} to="/wechat">
                    微信公众号
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">维护小程序、小程序级密钥</td>
                <td className="py-2">
                  <Link className={link} to="/miniprograms">
                    小程序管理
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">Android 机器人、转发任务队列、群发短链</td>
                <td className="py-2">
                  <Link className={link} to="/miniprograms">
                    小程序管理
                  </Link>
                  <span className="text-slate-400"> · </span>
                  <Link className={link} to="/robot-management">
                    机器人管理
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">金数据地址、流程开关、企微通知、诊断</td>
                <td className="py-2">
                  <Link className={link} to="/interfaces">
                    接入与推送
                  </Link>
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-2 pr-3">查 Webhook / 草稿失败原因</td>
                <td className="py-2">
                  <Link className={link} to="/log-center">
                    日志中心
                  </Link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="工作台 · 首页">
        <p className="text-sm leading-relaxed text-slate-700">
          登录后默认进入 <Link className={link} to="/dashboard">首页</Link>
          ，可查看登记条数、上线前三步检查（密钥 / 公众号 / 字段映射）与模块导览。
        </p>
      </Card>

      <Card title="账号与安全">
        <p className="text-sm leading-relaxed text-slate-700">
          手机访问公开首页时，点「登录后台」会进入手机专用登录页（亦可通过 <Link className={link} to="/m/login">/m/login</Link>
          ）；电脑访问则为常规登录页。会话过期后会按当前设备宽度跳回对应登录页。
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-700">
          左侧栏「当前账号」、或顶部栏用户名旁可打开「修改密码」。需填写当前密码，新密码至少 6 位；保存后会刷新本会话，其他终端需重新登录。
          系统管理员仍可在 <Link className={link} to="/users">用户管理</Link> 为普通用户重置或修改密码。
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-700">
          仅<strong className="font-semibold text-slate-900">系统管理员</strong>可见
          <Link className={link} to="/users">用户管理</Link>、
          <Link className={link} to="/login-logs">系统登录日志</Link>
          ；普通会员左侧菜单不会出现这两项（集中在「日志与账号」分组）。复杂配置与审计建议在电脑浏览器中完成。
        </p>
      </Card>

      <Card title="业务 · 信息登记">
        <p className="text-sm leading-relaxed text-slate-700">
          <Link className={link} to="/entries">信息登记</Link>
          列出金数据推送或手工维护的记录，支持搜索、编辑、删除、重新拉取图片、手动推草稿。草稿依赖公众号授权与图片成功落盘。
        </p>
      </Card>

      <Card title="站点与展示">
        <ul className="list-inside list-disc space-y-2 text-sm text-slate-700">
          <li>
            <Link className={link} to="/sites">站点管理</Link>：多站点、各站 Webhook 密钥、站点级 Android 机器人密钥。
          </li>
          <li>
            <Link className={link} to="/settings">内容与样式</Link>：字段映射、HTML 模板、贴图/图文体裁、AI 写标题等。
          </li>
          <li>
            <Link className={link} to="/wechat">微信公众号</Link>：第三方平台或 AppID+Secret 绑定。
          </li>
          <li>
            <Link className={link} to="/miniprograms">小程序管理</Link>：展示配置、站点与转发策略；侧栏
            <Link className={link} to="/robot-management">机器人管理</Link> 维护 Android 机器人。
          </li>
        </ul>
      </Card>

      <Card title="推广与机器人（Android）">
        <p className="text-sm leading-relaxed text-slate-700">
          在 <Link className={link} to="/miniprograms">小程序管理</Link> 列表可进入「站点与转发配置」管理各站策略与队列，或通过各行或列表上方的「转发日志」查看 Android 任务获取与完成时间线；
          <Link className={link} to="/robot-management">机器人管理</Link> 维护机器人编号与转发站点。
          设备端 App 在「接口配置」中填写机器人编号与密钥即可拉取任务（具体以当前发行版说明为准）。
        </p>
      </Card>

      <Card title="集成 · 接入与推送">
        <p className="mb-3 text-sm leading-relaxed text-slate-700">
          <Link className={link} to="/interfaces">接入与推送</Link>
          按标签页组织：<strong className="text-slate-900">流程与开关</strong>（是否入库、是否推草稿、可选先人工审核、待审企微 Webhook）、
          <strong className="text-slate-900">金数据接入</strong>（POST 地址与密钥）、
          <strong className="text-slate-900">自动化</strong>（草稿成功时企微通知、AI 标题）、
          <strong className="text-slate-900">敏感词管理</strong>（与「内容与样式」词表同步）、
          <strong className="text-slate-900">服务与诊断</strong>（上传目录、图片拉取诊断）、
          <strong className="text-slate-900">开放平台地址</strong>（票据回调、授权回调等）。
        </p>
      </Card>

      <Card title="监控 · 日志中心">
        <p className="mb-3 text-sm leading-relaxed text-slate-700">
          <Link className={link} to="/log-center">日志中心</Link>
          汇总金数据 Webhook、站长手机端登记与后台登记的信息入库与草稿推送结果；可按来源、入库结果与关键词筛选，并显示入库站点。
        </p>
        <p className="text-sm leading-relaxed text-slate-700">
          <Link className={link} to="/login-logs">系统登录日志</Link>
          （仅系统管理员）：后台登录成功/失败、IP、浏览器摘要，用于安全审计。
        </p>
      </Card>

      <Card title="推荐上线顺序">
        <ol className="list-inside list-decimal space-y-2 text-sm text-slate-700">
          <li>在站点管理中配置金数据 Webhook 密钥。</li>
          <li>在微信公众号中完成一种授权方式。</li>
          <li>在内容与样式中映射正文、图片等 field_xx。</li>
          <li>在接入与推送中确认数据流开关，并把金数据 POST 地址配好。</li>
          <li>测试一条表单提交，在信息登记与日志中心核对结果。</li>
        </ol>
      </Card>

      <Card title="与【微推广】的差异">
        <ul className="list-inside list-disc space-y-1 text-sm text-slate-700">
          <li>无交易金额与套餐天数计算；贴图虾含小程序侧机器人与 Android 任务下发。</li>
          <li>展示有效期由站点默认天数与条目日期等规则决定。</li>
          <li>内容通过接口写入微信公众号草稿箱，由小编在公众平台发布。</li>
        </ul>
      </Card>
    </div>
  );
}
